import { useState, useEffect, useCallback } from 'react';
import { Bell, Check, Trash2, X, CheckCheck, AlertCircle, FileText, Utensils, Activity, CreditCard, CheckCircle, XCircle, Clock, ClipboardCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { axiosInstance } from "../../App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function ParentNotifications({ user }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/parent/notifications');
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/parent/notifications/unread-count');
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'ibu_bapa') {
      fetchUnreadCount();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchUnreadCount]);

  const handleOpenDialog = () => {
    fetchNotifications();
    setShowDialog(true);
  };

  const markAsRead = async (notificationId) => {
    try {
      await axiosInstance.put(`/parent/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Ralat menanda notifikasi');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axiosInstance.put('/parent/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('Semua notifikasi ditanda dibaca');
    } catch (error) {
      toast.error('Ralat menanda semua notifikasi');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axiosInstance.delete(`/parent/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      fetchUnreadCount();
      toast.success('Notifikasi dipadam');
    } catch (error) {
      toast.error('Ralat memadam notifikasi');
    }
  };

  // Navigate to the relevant module when notification is clicked
  const handleNotificationClick = async (notification) => {
    console.log('[ParentNotifications] Notification clicked:', notification);
    
    // Mark as read first
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    
    // Close dialog
    setShowDialog(false);
    
    // Navigate based on notification type with deep linking
    const type = notification.type || '';
    const childId = notification.child_id;
    const data = notification.data || {};
    
    console.log('[ParentNotifications] Type:', type, 'ChildId:', childId, 'Data:', data);
    
    // Build query params for deep linking
    const buildParams = (params) => {
      const filtered = Object.entries(params).filter(([_, v]) => v);
      return filtered.length > 0 ? '?' + new URLSearchParams(filtered).toString() : '';
    };
    
    let targetUrl = '/';
    
    if (type.includes('activity')) {
      // Deep link to specific activity date and highlight activity
      const params = buildParams({
        date: data.date,
        child_id: childId,
        activity_id: data.activity_id,
        highlight: data.activity_id
      });
      targetUrl = `/activities${params}`;
    } else if (type.includes('daily_log')) {
      // Deep link to specific daily log date
      const params = buildParams({
        date: data.date,
        child_id: childId,
        log_id: data.log_id,
        highlight: data.log_id
      });
      targetUrl = `/daily-logs${params}`;
    } else if (type.includes('nutrition')) {
      // Deep link to specific nutrition/menu date
      const params = buildParams({
        date: data.date,
        child_id: childId,
        menu_id: data.nutrition_id || data.menu_id,
        highlight: data.nutrition_id || data.menu_id
      });
      targetUrl = `/nutrition${params}`;
    } else if (type === 'payment_success' || type === 'payment_update') {
      // Navigate to specific invoice if available
      const params = buildParams({ invoice_id: data.invoice_id });
      targetUrl = `/billing${params}`;
    } else if (type === 'payment_failed' || type === 'payment_reminder') {
      // For failed/abandoned payments, try to open the retry URL or navigate to invoices
      const retryUrl = data.retry_url;
      if (retryUrl) {
        console.log('[ParentNotifications] Opening retry URL:', retryUrl);
        window.open(retryUrl, '_blank');
        return; // Don't navigate
      } else {
        const params = buildParams({ invoice_id: data.invoice_id });
        targetUrl = `/billing${params}`;
      }
    } else if (type.includes('album')) {
      // Deep link to specific album
      if (data.album_id) {
        targetUrl = `/album/${data.album_id}`;
      } else {
        targetUrl = '/albums';
      }
    } else if (['survey_2027_reminder', 'survey_edit_reminder', 'survey_farewell', 'survey_access_ending'].includes(type) && data.link_path) {
      // Pengesahan Pelajar Sesi: buka borang terus melalui pautan peribadi
      targetUrl = data.link_path;
    } else if (childId) {
      // Default: go to child's nutritional status
      targetUrl = `/nutritional-status/${childId}`;
    }
    
    console.log('[ParentNotifications] Navigating to:', targetUrl);
    navigate(targetUrl);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'activity_new':
      case 'activity_update':
        return <Activity className="w-5 h-5 text-blue-500" />;
      case 'daily_log_new':
      case 'daily_log_update':
        return <FileText className="w-5 h-5 text-green-500" />;
      case 'nutrition_new':
      case 'nutrition_update':
        return <Utensils className="w-5 h-5 text-orange-500" />;
      case 'payment_success':
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'payment_failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'payment_reminder':
        return <Clock className="w-5 h-5 text-amber-500" />;
      case 'payment_update':
        return <CreditCard className="w-5 h-5 text-purple-500" />;
      case 'survey_2027_reminder':
      case 'survey_edit_reminder':
        return <ClipboardCheck className="w-5 h-5 text-pink-500" />;
      case 'survey_farewell':
      case 'survey_access_ending':
        return <Clock className="w-5 h-5 text-purple-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const getNotificationBg = (type, isRead) => {
    if (isRead) return 'bg-gray-50';
    switch (type) {
      case 'activity_new':
      case 'activity_update':
        return 'bg-blue-50 border-l-4 border-blue-400';
      case 'daily_log_new':
      case 'daily_log_update':
        return 'bg-green-50 border-l-4 border-green-400';
      case 'nutrition_new':
      case 'nutrition_update':
        return 'bg-orange-50 border-l-4 border-orange-400';
      case 'payment_success':
        return 'bg-emerald-50 border-l-4 border-emerald-400';
      case 'payment_failed':
        return 'bg-red-50 border-l-4 border-red-400';
      case 'payment_reminder':
        return 'bg-amber-50 border-l-4 border-amber-400';
      case 'payment_update':
        return 'bg-purple-50 border-l-4 border-purple-400';
      case 'survey_2027_reminder':
      case 'survey_edit_reminder':
        return 'bg-pink-50 border-l-4 border-pink-400';
      case 'survey_farewell':
      case 'survey_access_ending':
        return 'bg-purple-50 border-l-4 border-purple-400';
      default:
        return 'bg-gray-50 border-l-4 border-gray-400';
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Baru sahaja';
    if (diffMins < 60) return `${diffMins} minit lepas`;
    if (diffHours < 24) return `${diffHours} jam lepas`;
    if (diffDays < 7) return `${diffDays} hari lepas`;
    return date.toLocaleDateString('ms-MY', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (user?.role !== 'ibu_bapa') return null;

  return (
    <>
      {/* Notification Bell Button */}
      <button
        onClick={handleOpenDialog}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        aria-label="Notifikasi"
      >
        <Bell className="w-6 h-6 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-pink-500" />
                Notifikasi
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-full">
                    {unreadCount} baru
                  </span>
                )}
              </div>
              {notifications.some(n => !n.is_read) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  <CheckCheck className="w-4 h-4 mr-1" />
                  Tanda Semua Dibaca
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Tiada notifikasi</p>
              </div>
            ) : (
              <div className="space-y-3 py-2">
                {notifications.map(notification => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 rounded-xl transition-all cursor-pointer hover:shadow-lg ${getNotificationBg(notification.type, notification.is_read)} border border-gray-100`}
                  >
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center">
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Title Row */}
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-sm font-semibold ${notification.is_read ? 'text-gray-600' : 'text-gray-900'}`}>
                            {notification.title}
                          </p>
                          <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            {!notification.is_read && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                className="p-1.5 hover:bg-white rounded-full transition-colors"
                                title="Tanda dibaca"
                              >
                                <Check className="w-4 h-4 text-green-600" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteNotification(notification.id)}
                              className="p-1.5 hover:bg-white rounded-full transition-colors"
                              title="Padam"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Child Name */}
                        {notification.child_name && (
                          <p className="text-xs font-medium text-pink-600 mt-1">
                            👶 {notification.child_name}
                          </p>
                        )}
                        
                        {/* Message */}
                        <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                          {notification.message}
                        </p>
                        
                        {/* Footer Row */}
                        <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-400">
                            🕐 {formatTime(notification.created_at)}
                          </p>
                          <span className="text-xs font-medium text-blue-500 hover:text-blue-700 flex items-center gap-1">
                            Lihat butiran
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
